<template lang="pug">
.user-controls-wrapper
  .editor-switch(v-if='isEditable && isLargeDesktop')
    h5 Editor
    ToggleSwitch.editor(:value='inEditorMode', @toggled='toggleEditMode')
  transition(name='wobble')
    .re-trigger-onboarding-popup(
      @click='showCompanyOnboardingPopupDetails',
      v-if='showReTriggerOnboardingPopup && isDesktopDevice',
      :class='{ "non-editable-section": inEditorMode }'
    )
      h5(v-tooltip.bottom='{ content: $t("navigation.watchIntroVideo") }') {{ $t('intro') }}
  UserLevelPoints(
    v-if='hasValidUser && isDesktopDevice && !isKnowledgeCheckCompany',
    :class='{ "non-editable-section": inEditorMode }'
  )
  Search.search(v-if='!isKnowledgeCheckCompany && isDesktopDevice', :class='{ "non-editable-section": inEditorMode }')
  router-link.bookmarks(
    :to='{ name: "AllUserBookmarks" }',
    data-cy='bookmarks',
    v-if='hasValidUser && isDesktopDevice && !isKnowledgeCheckCompany',
    :class='{ "non-editable-section": inEditorMode }'
  )
    .bookmarks(@mouseleave='hoveringBookmark = false', @mouseover='hoveringBookmark = true')
      SVGRenderer(
        :icon='allBookmarks',
        :toggle-hover='hoveringBookmark',
        height='20px',
        width='20px',
        stroke-color='var(--hero-foreground-color)',
        stroke-hover-color='var(--hero-foreground-color)',
        v-tooltip.bottom='{ content: $t("bookmarks.allBookmarks") }'
      )
  template
    .user-icon-area-wrapper
      .user-icon-area(data-cy='user-icon-area', v-if='hasValidUser')
        .user-icon.with-initials(@click='$emit("toggle-user-dropdown")', v-if='initials')
          h6 {{ initials }}
        .user-icon(@click='$emit("toggle-user-dropdown")', v-else)
          SVGRenderer(
            :fill-color='"var(--primary-color)"',
            :has-hover='false',
            :icon='defaultUserIcon',
            :stroke-color='"var(--primary-foreground-color)"',
            width='30px',
            height='30px'
          )
      router-link.sign-in.h5(to='/login', v-else) {{ $t('onboarding.login') }}
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'

  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import UserLevelPoints from '@/components/UserLevelPoints.vue'
  import Search from '@/components/Search.vue'
  import ToggleSwitch from '@/components/common/ToggleSwitch.vue'

  import useIcons from '@/composables/useIcons'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useEditor from '@/composables/useEditor'
  import { useRoute } from 'vue-router/composables'

  import { UserModule } from '@/store/modules/user'
  import { CourseModule } from '@/store/modules/course'
  import { EditorModule } from '@/store/modules/editor'

  const route = useRoute()
  const { defaultUserIcon, allBookmarks } = useIcons()
  const { isKnowledgeCheckCompany, showCompanyOnboardingPopupDetails, showReTriggerOnboardingPopup, hasValidUser } =
    useCommonMixin()
  const { isDesktopDevice, isLargeDesktop } = useBreakpoint()
  const { onEditorPages, inEditorMode } = useEditor(route)

  defineEmits(['toggle-user-dropdown'])

  const hoveringBookmark = ref(false)

  const isEditable = computed(() => {
    return onEditorPages.value && CourseModule.course?.isEditable
  })

  const initials = computed((): string => {
    return UserModule.initials || ''
  })

  const toggleEditMode = (value: boolean) => {
    EditorModule.setEditMode(value)
  }
</script>

<style lang="postcss">
  .user-controls-wrapper {
    @apply ketch-flex ketch-items-center ketch-justify-end ketch-h-c30 ketch-space-x-c20 ketch-relative;

    @screen md {
      @apply ketch-relative;
    }

    .editor-switch {
      @apply ketch-flex ketch-items-center ketch-space-x-c15;

      h5 {
        @apply ketch-text-hero-foreground-color;
      }
    }

    .re-trigger-onboarding-popup {
      @apply ketch-px-c5 ketch-border ketch-rounded-[5px] ketch-cursor-pointer;
      @apply ketch-border-hero-foreground-color ketch-text-hero-foreground-color;
    }

    .user-level-points {
      @apply ketch-relative;
    }

    .search {
      @apply ketch-hidden ketch-relative md:ketch-block;
    }

    .user-icon-area-wrapper {
      .user-icon-area {
        .user-icon {
          @apply ketch-w-c30 ketch-h-c30 ketch-cursor-pointer;

          &.with-initials {
            @apply ketch-flex ketch-items-center ketch-justify-center ketch-rounded-full;
            @apply ketch-text-primary-foreground-color ketch-bg-primary-color hover:ketch-bg-primary-hover-color;
          }
        }
      }

      .sign-in {
        @apply ketch-cursor-pointer ketch-text-hero-foreground-color;
      }
    }
  }
</style>
